"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.BaseApi = void 0;
const axios_1 = require("axios");
const errorHandler_1 = require("../../public/services/errorHandler");
const authData_1 = require("../ephemeral/authData");
const jwtDecode = require("jwt-decode");
const auth0_1 = require("../utils/auth0");
/**
 * @description: lowest level api calls using Axios module. For docs, see: https://github.com/axios/axios
 *
 * @export
 * @abstract
 * @class BaseApi
 * @template T
 */
class BaseApi {
    /**
     *Creates an instance of BaseApi.
     * @param {AxiosRequestConfig} config
     * @memberof BaseApi
     */
    constructor(config) {
        this.http = axios_1.default.create(config);
        this.http.interceptors.response.use(undefined, (error) => {
            this.handleError(error);
            return Promise.reject(error);
        });
        // Add a request interceptor
        this.http.interceptors.request.use((config) => __awaiter(this, void 0, void 0, function* () {
            if (config.headers["Authorization"]) {
                const used_token = config.headers["Authorization"].replace("Bearer ", "");
                // temporary solution
                try {
                    // @ts-ignore
                    const d = jwtDecode(used_token);
                    if (d.exp < Date.now() / 1000) {
                        console.log("using expired token");
                        // check if there is a newer token in the auth (to avoid api consumer constantly refreshing token)
                        if (authData_1.Auth.getUserData().exp > Date.now() / 1000) {
                            console.log("there is a non expired token on the auth service, using that");
                            config.headers["Authorization"] = "Bearer " + authData_1.Auth.getUserToken();
                        }
                        else if (authData_1.Auth.getUserRefreshToken()) {
                            console.log("There is a refresh token, using that, and update auth service");
                            try {
                                const refreshed_token = yield auth0_1.default(authData_1.Auth.getUserRefreshToken());
                                authData_1.Auth.setAccessToken(refreshed_token.access_token);
                                authData_1.Auth.setUserToken(refreshed_token.id_token);
                                config.headers["Authorization"] = "Bearer " + refreshed_token.id_token;
                            }
                            catch (e) {
                                // ..
                            }
                        }
                        else {
                            console.log("no valid token found, no refresh token found, sending a request that is doomed to fail");
                        }
                    }
                }
                catch (e) {
                    //...
                }
            }
            return config;
        }), (error) => {
            // Do something with request error
            this.handleError(error);
            return Promise.reject(error);
        });
        // comment this out for request logging
        // this.http.interceptors.request.use(
        //   (c: AxiosRequestConfig) => {
        //     if (c.method && c.url) {
        //       console.log(
        //         "\x1b[45m",
        //         c.method.toUpperCase() + " - " + c.url + ":" + JSON.stringify(c.params)
        //       );
        //     }
        //     return c;
        //   },
        //   error => {
        //     return Promise.reject(error);
        //   }
        // );
    }
    /**
     * @description simple promise based getter for one object.
     *
     * @template R
     * @param {string} urlSuffix : endpoint name
     * @param {object} params: get query params
     * @returns {Promise<R>} : promise of same type as instantiated @class BaseApi
     * @memberof BaseApi
     */
    get(urlSuffix, params = {}) {
        return this.http.get(urlSuffix, { params: params }).then((r) => r.data);
    }
    /**
     * @description simple promise based getter for multiple object.
     *
     * @template R
     * @param {string} urlSuffix
     * @param {{ [key: string]: any }} [httpParams] plain key-valiue object
     * @returns {Promise<R[]>}: promise of same type as instantiated @class BaseApi
     * @memberof BaseApi
     */
    getAll(urlSuffix, httpParams) {
        return this.http.get(urlSuffix, { params: httpParams }).then((r) => r.data);
    }
    /**
     * @description simple post method for one object.
     *
     * @template R
     * @param {string} urlSuffix
     * @param {object} body
     * @returns {Promise<R>} promise of same type as instantiated @class BaseApi, created object is returned.
     * @memberof BaseApi
     */
    post(urlSuffix, body, config) {
        return this.http.post(urlSuffix, body, config).then((r) => r.data);
    }
    /**
     * @description simple post method for multiple objects
     *
     * @template R
     * @param {string} urlSuffix
     * @param {object} body
     * @returns {Promise<R[]>} promise of same type as instantiated @class BaseApi. Created objects are returned
     * @memberof BaseApi
     */
    postMany(urlSuffix, body) {
        return this.http.post(urlSuffix, body).then((r) => r.data);
    }
    /**
     * @description single update
     *
     * @template R
     * @param {string} urlSuffix
     * @param {object} body
     * @returns {Promise<R>} promise of same type as instantiated @class BaseApi, updated object is returned.
     * @memberof BaseApi
     */
    put(urlSuffix, body) {
        return this.http.put(urlSuffix, body).then((r) => r.data);
    }
    /**
     * @description single update
     *
     * @template R
     * @param {string} urlSuffix
     * @param {object} body
     * @returns {Promise<R>} promise of same type as instantiated @class BaseApi, updated object is returned.
     * @memberof BaseApi
     */
    patch(urlSuffix, body) {
        return this.http.patch(urlSuffix, body).then((r) => r.data);
    }
    /**
     * @description multiple updates
     *
     * @template R
     * @param {string} urlSuffix
     * @param {object} body
     * @returns {Promise<R[]>} promise of same type as instantiated @class BaseApi. Updated objects are returned
     * @memberof BaseApi
     */
    putMany(urlSuffix, body) {
        return this.http.put(urlSuffix, body).then((r) => r.data);
    }
    /**
     * @description simple delete method that resolves to return void return value on succes.
     *
     * @param {string} urlSuffix
     * @returns {(Promise<void>)}
     * @memberof BaseApi
     */
    delete(urlSuffix, body) {
        return this.http
            .delete(urlSuffix, body)
            .then((r) => r.status === 200 || r.status === 204 ? Promise.resolve(undefined) : Promise.reject(r));
    }
    /**
     * @description verbose logging of non-succes/non-200 status codes, network errors, or other errors.
     *
     * @private
     * @param {AxiosError} error
     * @memberof BaseApi
     */
    handleError(error) {
        errorHandler_1.EH.error(error);
        if (error.response) {
            // The request was made and the server responded with a status code
            // that falls out of the range of 2xx
            console.log("backend error response");
            console.log("response data:\n" + JSON.stringify(error.response.data));
            console.log("response status", error.response.status);
            console.log("response headers", error.response.headers);
            // console.log("response request full config", error.config );
            console.log(`failed request ${error.config.method} ${error.config.url} `);
            console.log('failed request params', error.config.params);
        }
        else if (error.request) {
            //  console.log( error );
            // The request was made but no response was received
            // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
            // http.ClientRequest in node.js
            console.log("attempted to make request but failed on client-side. No response was received.");
            // console.log(error.request);
        }
        else {
            // Something happened in setting up the request that triggered an Error
            console.log("Error", error.message);
        }
    }
}
exports.BaseApi = BaseApi;
//# sourceMappingURL=data:application/json;base64,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